import { useRef, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ImagePlus, Loader2 } from "lucide-react";

/**
 * Reusable image upload widget.
 *
 * Props:
 *   endpoint     — POST target, e.g. `/files/team/${teamId}` (relative to /api)
 *   currentPath  — existing storage_path so we can render current image
 *   onUploaded   — callback ({ storage_path, url }) after success
 *   shape        — "square" | "circle"  (visual shape of thumbnail)
 *   label        — button label
 *   testId       — data-testid prefix
 */
export default function ImageUpload({ endpoint, currentPath, onUploaded, shape = "square", label = "Upload image", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewPath, setPreviewPath] = useState(currentPath || null);

  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const srcFor = (p) => `${BACKEND_URL}/api/files/${p}`;

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-picked
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreviewPath(r.data.storage_path);
      onUploaded && onUploaded(r.data);
      toast.success("Image uploaded");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const rounded = shape === "circle" ? "rounded-full" : "rounded-md";

  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <button
        type="button"
        onClick={pick}
        className={`w-16 h-16 ${rounded} border-2 border-dashed border-border hover:border-[#83B68B] transition-colors flex items-center justify-center bg-muted overflow-hidden relative`}
        data-testid={`${testId}-thumb`}
        aria-label={label}
      >
        {previewPath ? (
          <img src={srcFor(previewPath)} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImagePlus className="w-5 h-5 opacity-50" />
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Loader2 className="w-5 h-5 animate-spin text-white" />
          </div>
        )}
      </button>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
          data-testid={`${testId}-btn`}
        >
          <Upload className="w-3 h-3" /> {previewPath ? "Replace" : label}
        </button>
        <div className="text-[10px] opacity-50">PNG/JPG/WEBP · max 5 MB</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

/** Small inline thumb for cards (auth-gated download via the session cookie). */
export function StorageImage({ path, alt = "", className = "" }) {
  if (!path) return null;
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  return <img src={`${BACKEND_URL}/api/files/${path}`} alt={alt} className={className} />;
}
